import React, { FC, PropsWithChildren, useEffect } from 'react';
import { Editor, Element, Frame, useEditor } from '@craftjs/core';
import { Container } from '../controls/Containers/Container';
import { UiEditorProps } from './uiEditor';
import { TopBar } from '../toolbox/topBar.component';
import { LeftBar } from '../toolbox/leftBar.component';
import styles from './uiEditor.module.scss';
import { formBuilderControlsResolver } from '../controls/resolver';
import { RightBar } from '../toolbox/rightBar.component';
import { RenderNode } from '../toolbox/renderNodes/renderNode';
import { FormFillContext } from './provider/formFill.context';
import { useContextSelector } from 'use-context-selector';
import { FormFillingNavigationBar } from './formFillingNavigation/formFillingNavigationBar/FormFillingNavigationBar';
import { FormConfigContextProvider } from './formConfigContext/formConfig.context';
import { EMPTY_LAYOUT } from './uiEditor-helper';
import { useSelectors as useSelectors } from '../controls/selectors';
import { CommonLayerItem } from '../toolbox/components/layerItem';
import { GeneralSettings } from '../toolbox/settingsPanel/GeneralSettings';

export const UiEditorComponent: FC<UiEditorProps> = React.memo(function UIEditor(props) {
  const setNodes = useContextSelector(FormFillContext, (x) => x.setNodes);

  return (
    <Editor
      resolver={formBuilderControlsResolver}
      enabled={props.isInDesignerMode}
      onRender={RenderNode}
      indicator={{
        success: '#09a07c',
        error: '#e75223',
        thickness: 4,
        transition: 'unset',
      }}
      onNodesChange={({ getSerializedNodes }) => setNodes?.(getSerializedNodes())}
    >
      <FormConfigContextProvider>
        <UiEditorInnerComponent {...props} />
      </FormConfigContextProvider>
    </Editor>
  );
});

// I needed extract the inner part of UiEditor to can use {@link useEditor}
const UiEditorInnerComponent: FC<UiEditorProps> = React.memo((props) => {
  const { actions, connectors } = useEditor();
  const selectors = useSelectors();
  useEffect(() => {
    actions.deserialize(JSON.stringify(props.isInDesignerMode ? EMPTY_LAYOUT : props.layoutScheme));
  }, [actions, props.isInDesignerMode, props.layoutScheme]);

  if (props.isInDesignerMode) {
    return (
      <>
        <TopBar />
        <div id={'page-container'} className={styles.editorDesigner}>
          <LeftBar selectors={selectors} layerItem={LayerItem} />
          <div
            data-test-id={'ui-editor-container'}
            className={styles.editorBodyDesigner}
            ref={(ref) => {
              if (!ref) return;
              connectors.select(connectors.hover(ref, ''), '');
            }}
          >
            <Frame>
              <Element id={'rootCanvas'} canvas={true} is={Container} flexBoxGap={12} />
            </Frame>
          </div>
          <RightBar formConfigSettingPanel={<GeneralSettings />} />
        </div>
      </>
    );
  }

  return (
    <div id="page-container" className={styles.editorContainer}>
      <div className={styles.navBar}>
        <FormFillingNavigationBar />
      </div>
      <div
        // NOTE: The ID is used to navigate the nav panel
        id={'ui-editor-container'}
        className={styles.editor}
        data-test-id={'ui-editor-container'}
      >
        <Frame>
          <Element canvas={true} id={'rootCanvas'} is={Container} />
        </Frame>
      </div>
    </div>
  );
});

const LayerItem: FC<PropsWithChildren> = ({ children }) => {
  const selectors = useSelectors();
  return <CommonLayerItem selectors={selectors}>{children}</CommonLayerItem>;
};
